import React, {useCallback, useMemo, useState} from 'react';
import {Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography} from '@mui/material';
import {
    CheckCircleOutline,
    DeleteOutline,
    ForwardToInbox,
    InfoOutlined,
    LinkOff,
    MoreVert,
    OpenInNew,
    PersonAddAlt1,
    PersonOff,
} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {CustomerMemberShipNames} from '../../../../../../../core/constants/enums';
import {routeFunctions} from '../../../../../../../core/models/static/routes/app-routes';

/**
 * Overflow menu of status-aware actions for a member row.
 *
 * @param {object} props
 * @param {any} props.member
 * @param {(member: any) => void} props.onAssign
 * @param {(member: any) => void} props.onDetach
 * @param {(member: any) => void} props.onDeactivate
 * @param {(member: any) => void} props.onActivate
 * @param {(member: any) => void} props.onDelete
 * @param {(member: any) => void} [props.onResendActivation]
 * @param {boolean} props.canAssign
 * @param {boolean} props.canOpenProfile
 * @param {boolean} [props.hasB2cWebsite]
 */
const MemberRowActionsMenu = ({
                                  member,
                                  onAssign,
                                  onDetach,
                                  onDeactivate,
                                  onActivate,
                                  onDelete,
                                  onResendActivation,
                                  canAssign,
                                  canOpenProfile,
                                  hasB2cWebsite,
                              }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const hasCustomer = !!(member?.customer?.id || member?.customer?.code);
    const isOnHold = member?.memberShipName === CustomerMemberShipNames.onHold;
    const isActive = member?.memberShipName === CustomerMemberShipNames.active;
    const isDeactivated = member?.memberShipName === CustomerMemberShipNames.deactivated;
    const isOnHoldOrWithoutCustomer =
        member?.memberShipName === CustomerMemberShipNames.onHold
        || member?.memberShipName === CustomerMemberShipNames.onHoldCompanyLost
        || !hasCustomer;

    const items = useMemo(() => {
        const next = [];
        if (canOpenProfile && hasCustomer && member?.customer?.id) {
            next.push({
                id: 'openProfile',
                label: t('rowActions.openProfile'),
                description: t('rowActions.descriptions.openProfile'),
                Icon: OpenInNew,
                component: Link,
                to: routeFunctions.customer.generalInformation.profile(member.customer.id),
            });
        }
        if (canAssign && isOnHoldOrWithoutCustomer) {
            next.push({
                id: 'assign',
                label: t('rowActions.assign'),
                description: t('rowActions.descriptions.assign'),
                Icon: PersonAddAlt1,
                onClick: () => onAssign(member),
            });
        }
        if (hasB2cWebsite && isOnHold) {
            next.push({
                id: 'resendActivation',
                label: t('rowActions.resendActivation'),
                description: t('rowActions.descriptions.resendActivation'),
                Icon: ForwardToInbox,
                onClick: () => onResendActivation?.(member),
            });
        }
        if (isDeactivated) {
            next.push({
                id: 'activate',
                label: t('rowActions.activate'),
                description: t('rowActions.descriptions.activate'),
                Icon: CheckCircleOutline,
                onClick: () => onActivate(member),
            });
        }
        if (hasCustomer) {
            next.push({
                id: 'detach',
                label: t('rowActions.detach'),
                description: t('rowActions.descriptions.detach'),
                Icon: LinkOff,
                onClick: () => onDetach(member),
            });
        }
        if (isActive) {
            next.push({
                id: 'deactivate',
                label: t('rowActions.deactivate'),
                description: t('rowActions.descriptions.deactivate'),
                Icon: PersonOff,
                onClick: () => onDeactivate(member),
            });
        }
        if (!isActive) {
            next.push({
                id: 'delete',
                label: t('rowActions.delete'),
                description: t('rowActions.descriptions.delete'),
                Icon: DeleteOutline,
                destructive: true,
                onClick: () => onDelete(member),
            });
        }
        return next;
    }, [
        canAssign,
        canOpenProfile,
        hasB2cWebsite,
        hasCustomer,
        isActive,
        isDeactivated,
        isOnHold,
        isOnHoldOrWithoutCustomer,
        member,
        onActivate,
        onAssign,
        onDeactivate,
        onDelete,
        onDetach,
        onResendActivation,
        t,
    ]);

    /**
     * Opens the overflow menu.
     * @type {(function(React.MouseEvent<HTMLElement>): void)}
     */
    const handleOpen = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    /**
     * Closes the overflow menu.
     * @type {function(): void}
     */
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    /**
     * Runs a menu item action then closes the menu.
     * @type {(function(function(): void): function(): void)}
     */
    const handleItemClick = useCallback((onClick) => () => {
        onClick?.();
        handleClose();
    }, [handleClose]);

    /**
     * Keeps the info icon from running the menu action or following a link.
     * @type {(function(React.MouseEvent): void)}
     */
    const stopAction = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
    }, []);

    return (
        <>
            <IconButton
                size="small"
                color="secondary"
                aria-label={t('rowActions.menu')}
                aria-controls={open ? `member-actions-${member.id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleOpen}
            >
                <MoreVert fontSize="small"/>
            </IconButton>
            <Menu
                id={`member-actions-${member.id}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                {items.map((item) => {
                    const Icon = item.Icon;
                    return (
                        <MenuItem
                            key={item.id}
                            component={item.component}
                            to={item.to}
                            onClick={item.component ? handleClose : handleItemClick(item.onClick)}
                            sx={{
                                gap: 1,
                                ...(item.destructive ? {color: 'error.main'} : {}),
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 36,
                                color: item.destructive ? 'error.main' : 'inherit',
                            }}>
                                <Icon fontSize="small"/>
                            </ListItemIcon>
                            <Box component="span" sx={{flex: 1}}>
                                {item.label}
                            </Box>
                            <Tooltip
                                arrow
                                enterTouchDelay={0}
                                title={(
                                    <Typography variant="body2" component="p" sx={{maxWidth: 320, py: 0.25}}>
                                        {item.description}
                                    </Typography>
                                )}
                            >
                                <Box
                                    component="span"
                                    aria-label={item.description}
                                    onClick={stopAction}
                                    onMouseDown={stopAction}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: 'text.secondary',
                                        lineHeight: 0,
                                    }}
                                >
                                    <InfoOutlined sx={{fontSize: 18}}/>
                                </Box>
                            </Tooltip>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default MemberRowActionsMenu;
